// Utility to determine and format a property's price in its original or converted currency

const CURRENCY_MAP = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$', EUR: '€' };
const LOCALE_MAP = { GBP: 'en-GB', INR: 'en-IN', CAD: 'en-CA', USD: 'en-US', EUR: 'de-DE' };

// Default exchange rates from GBP base
const DEFAULT_RATES = { GBP: 1, INR: 106, CAD: 1.72, USD: 1.27, EUR: 1.17 };

const INDIA_CITIES = ['mumbai', 'delhi', 'bangalore', 'bengaluru', 'chennai', 'kolkata', 'pune', 'hyderabad', 'ahmedabad', 'jaipur', 'lucknow', 'noida', 'gurgaon', 'gurugram', 'surat', 'chandigarh', 'indore', 'nagpur', 'coimbatore', 'kochi', 'patna', 'bhopal'];
const CANADA_CITIES = ['toronto', 'vancouver', 'montreal', 'ottawa', 'calgary', 'edmonton', 'winnipeg', 'quebec', 'hamilton', 'victoria', 'halifax', 'saskatoon', 'regina'];
const UK_CITIES = ['london', 'manchester', 'birmingham', 'leeds', 'glasgow', 'edinburgh', 'liverpool', 'bristol', 'sheffield', 'oxford', 'cambridge', 'nottingham', 'leicester', 'coventry', 'cardiff', 'belfast', 'brighton', 'southampton', 'bath'];

// Country to currency mapping
const COUNTRY_CURRENCY = { UK: 'GBP', IN: 'INR', CA: 'CAD' };

export const getPropertyCurrency = (property) => {
  if (property.currency && CURRENCY_MAP[property.currency]) return property.currency;
  const countryMap = { UK: 'GBP', IN: 'INR', CA: 'CAD', US: 'USD' };
  if (property.country && countryMap[property.country]) return countryMap[property.country];
  const city = (property.city || '').toLowerCase();
  if (INDIA_CITIES.some(c => city.includes(c))) return 'INR';
  if (CANADA_CITIES.some(c => city.includes(c))) return 'CAD';
  if (UK_CITIES.some(c => city.includes(c))) return 'GBP';
  if (property.price > 500000) return 'INR';
  return 'GBP';
};

/**
 * Convert price from one currency to another using exchange rates.
 * @param {number} price - Original price
 * @param {string} fromCurrency - Source currency code (e.g., 'GBP')
 * @param {string} toCurrency - Target currency code (e.g., 'INR')
 * @param {object} rates - Exchange rates from admin settings (GBP base)
 */
export const convertPrice = (price, fromCurrency, toCurrency, rates) => {
  if (!price || fromCurrency === toCurrency) return price;
  const r = rates || DEFAULT_RATES;
  // Convert to GBP first, then to target
  const fromRate = r[fromCurrency] || 1;
  const toRate = r[toCurrency] || 1;
  return Math.round((price / fromRate) * toRate);
};

export const formatPropertyPrice = (price, currency) => {
  const symbol = CURRENCY_MAP[currency] || '£';
  const locale = LOCALE_MAP[currency] || 'en-GB';
  if (!price && price !== 0) return symbol + '0';
  return symbol + new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(price);
};

/**
 * Format price with auto-conversion to viewer's selected country currency.
 */
export const formatConvertedPrice = (price, propertyCurrency, viewerCountry, rates) => {
  const viewerCurrency = COUNTRY_CURRENCY[viewerCountry] || 'GBP';
  const converted = convertPrice(price, propertyCurrency, viewerCurrency, rates);
  return formatPropertyPrice(converted, viewerCurrency);
};

export const formatPriceForProperty = (property) => {
  const currency = getPropertyCurrency(property);
  return formatPropertyPrice(property.price, currency);
};

/**
 * Format area size with country-specific units.
 * UK/Canada: sq ft, India: sq ft (with sq m equivalent)
 */
export const formatAreaSize = (sqft, country) => {
  if (!sqft) return '';
  const formatted = Number(sqft).toLocaleString('en-GB');
  if (country === 'IN') {
    const sqm = Math.round(sqft * 0.0929);
    return `${formatted} sq ft (${sqm.toLocaleString('en-IN')} sq m)`;
  }
  return `${formatted} sq ft`;
};
